import React, { useEffect, useRef, useState } from "react";
import {
  IModalProps,
  Modal,
  styled,
} from "../../../seenspire-library";
import {
  CodeContainer,
  CodeDivider,
  CodeInput,
  Container,
  ModalCustomStyles,
  ModalSubtitle,
  ModalTitle,
  StyledCtaButton,
  StyledInput,
} from "./styles";
import { WarningPanel } from "../../containers/Auth/Login/Login";

const LinkStyled = styled("a")`
  color: rgb(146, 160, 175);
`;

interface AddScreenModal extends IModalProps {
  onSubmit: (code: string, screenName: string) => void;
  error: string | null;
  isOpen: boolean;
}

const AddScreenModal = (props: AddScreenModal) => {
  const { onSubmit, error, isOpen } = props;

  const codeLength = 6; 
  const [code, setCode] = useState(Array(codeLength).fill(""));
  const [screenName, setScreenName] = useState("");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!isOpen) {
      setCode(Array(codeLength).fill(""));
      setScreenName('');
    }
  }, [isOpen])

  const handleChange = (value: string, index: number) => {
    if (value.length <= 1) {
      const newCode = [...code];
      newCode[index] = value.toUpperCase();
      setCode(newCode);

      if (value && index < codeLength - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowLeft" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData
      .getData("Text")
      .slice(0, codeLength)
      .split("");
    const newCode = Array(codeLength).fill("");

    pastedData.forEach((char, idx) => {
      newCode[idx] = char.toUpperCase();
    });

    setCode(newCode);

    if (inputRefs.current[0]) {
      inputRefs.current[newCode.length - 1]?.focus();
    }
  };

  return (
    <Modal customStyle={ModalCustomStyles} {...props}>
      <Container>
        <ModalTitle>Add Screen</ModalTitle>
        <ModalSubtitle color="gray">
          Open your TV/device's browser and navigate to <LinkStyled href="https://player.seenspire.com" target="_blank" rel="noreferrer">player.seenspire.com</LinkStyled>.
        </ModalSubtitle>

        <CodeContainer>
          {code.map((value, index) => (
            <>
              <CodeInput
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                maxLength={1}
                value={value}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
              />
              {index !== code.length - 1 && <CodeDivider />}
            </>
          ))}
        </CodeContainer>
        {error && <WarningPanel>{error}</WarningPanel>}

        <StyledInput
          placeholder="Add screen name"
          value={screenName}
          onChange={(e) => setScreenName(e.target.value)}
        />
        <StyledCtaButton
          buttonType="cta"
          onClick={() => onSubmit(code.join(""), screenName)}
          disabled={!code || code.some((_code) => !_code || _code === "") || !screenName}
        >
          Add Screen
        </StyledCtaButton>
      </Container>
    </Modal>
  );
};

export default AddScreenModal;
